import { LAST_ACTIVITY_KEY } from '../../shared-state/identity/constants/activityTimerConstants';
import type { IdleChainStage, PresenceRule, TimeoutAction } from './inactivityRules';

export const PRESENCE_PROMPT_STORAGE_KEY = 'presencePrompt';
const PRESENCE_PROMPT_CLAIM_KEY = 'presencePromptClaim';
export const PRESENCE_PROMPT_OUTCOME_STORAGE_KEY = 'presencePromptOutcome';
const IDLE_CHAIN_STAGE_KEY = 'idleChainStage';
export const IDLE_CHAIN_STAGE_STORAGE_KEY = IDLE_CHAIN_STAGE_KEY;
const IDLE_CHAIN_STAGE_AT_KEY = 'idleChainStageAt';
const IDLE_CHAIN_RETURNED_KEY = 'idleChainReturnedAt';
export const IDLE_CHAIN_RETURNED_STORAGE_KEY = IDLE_CHAIN_RETURNED_KEY;
export const OOO_SELECTED_MANUALLY_KEY = 'isUserSelectedOooManually';
export const AWAY_SELECTED_MANUALLY_KEY = 'isUserSelectedAwayManually';

const PRESENCE_RULES: PresenceRule[] = ['idle-chain', 'manual-ooo', 'midnight'];
const TIMEOUT_ACTIONS: TimeoutAction[] = ['none', 'lobby-return-green', 'lobby-keep-status', 'sign-out', 'midnight-keep-ooo', 'midnight-set-ooo-today'];

export interface IPresencePrompt {
    rule: PresenceRule;
    deadline: number;
}

// localStorage can throw (private mode, blocked storage). Failing reads behave as "nothing stored".
function safeGet(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function safeSet(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        // storage unavailable: rules behave as if the user were active
    }
}

function safeRemove(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch {
        // storage unavailable
    }
}

export function readLastActivity(now: number): number {
    const value = parseInt(safeGet(LAST_ACTIVITY_KEY) ?? '', 10);
    return Number.isFinite(value) ? value : now;
}

export function writeLastActivity(now: number): void {
    safeSet(LAST_ACTIVITY_KEY, String(now));
}

export function readIdleChainStage(): IdleChainStage | null {
    const value = safeGet(IDLE_CHAIN_STAGE_KEY);
    return value === 'away' || value === 'ooo' ? value : null;
}

export function writeIdleChainStage(stage: IdleChainStage): void {
    // timestamp first, so a tab reacting to the stage's storage event already sees when it was written
    safeSet(IDLE_CHAIN_STAGE_AT_KEY, String(Date.now()));
    safeSet(IDLE_CHAIN_STAGE_KEY, stage);
}

export function clearIdleChainStage(): void {
    safeRemove(IDLE_CHAIN_STAGE_KEY);
    safeRemove(IDLE_CHAIN_STAGE_AT_KEY);
}

/** Epoch ms the current idle-chain stage was written, or null if unknown/missing (e.g. an older tab's stage). */
export function readIdleChainStageAt(): number | null {
    const value = parseInt(safeGet(IDLE_CHAIN_STAGE_AT_KEY) ?? '', 10);
    return Number.isFinite(value) ? value : null;
}

/** Marks that this tab returned the chain to available, so other tabs stop waiting for a stage sync. */
export function markIdleChainReturned(now: number): void {
    safeSet(IDLE_CHAIN_RETURNED_KEY, String(now));
}

export function isOooSelectedManually(): boolean {
    return safeGet(OOO_SELECTED_MANUALLY_KEY) === 'true';
}

export function clearOooSelectedManually(): void {
    safeRemove(OOO_SELECTED_MANUALLY_KEY);
}

/** Marks the current out-of-office as one the manual-OOO 4h rule handles (the midnight rule's OOO, besides the user's own). */
export function markOooSelectedManually(): void {
    safeSet(OOO_SELECTED_MANUALLY_KEY, 'true');
}

export function isAwaySelectedManually(): boolean {
    return safeGet(AWAY_SELECTED_MANUALLY_KEY) === 'true';
}

/** Raw manual-selection flag ("true", legacy "false", or null), for the one-time legacy stage migration. */
export function readManualFlag(key: typeof AWAY_SELECTED_MANUALLY_KEY | typeof OOO_SELECTED_MANUALLY_KEY): string | null {
    return safeGet(key);
}

export function removeManualFlag(key: typeof AWAY_SELECTED_MANUALLY_KEY | typeof OOO_SELECTED_MANUALLY_KEY): void {
    safeRemove(key);
}

// Whether this tab currently shows a presence prompt. Per tab (module state), not shared: SocketProvider only
// ignores activity in a tab that shows the prompt, so e.g. the lobby keeps recording activity.
let isPromptVisibleInTab = false;

export function setPromptVisibleInThisTab(isVisible: boolean): void {
    isPromptVisibleInTab = isVisible;
}

export function isPromptVisibleInThisTab(): boolean {
    return isPromptVisibleInTab;
}

export function readPrompt(): IPresencePrompt | null {
    const raw = safeGet(PRESENCE_PROMPT_STORAGE_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (PRESENCE_RULES.includes(parsed?.rule) && typeof parsed?.deadline === 'number') {
            return { rule: parsed.rule, deadline: parsed.deadline };
        }
    } catch {
        // malformed value: treat as no prompt
    }
    return null;
}

export function writePrompt(prompt: IPresencePrompt): void {
    safeSet(PRESENCE_PROMPT_STORAGE_KEY, JSON.stringify(prompt));
}

export function clearPrompt(): void {
    safeRemove(PRESENCE_PROMPT_STORAGE_KEY);
}

/**
 * Best-effort cross-tab lock so only one tab runs a prompt's timeout action.
 * Not atomic across tabs, but tabs check on different ticks so collisions are unlikely.
 */
export function claimPromptTimeout(tabId: string, deadline: number): boolean {
    const claim = `${tabId}:${deadline}`;
    const existing = safeGet(PRESENCE_PROMPT_CLAIM_KEY);
    if (existing && existing !== claim && existing.endsWith(`:${deadline}`)) return false;
    safeSet(PRESENCE_PROMPT_CLAIM_KEY, claim);
    return safeGet(PRESENCE_PROMPT_CLAIM_KEY) === claim;
}

export interface IPromptOutcome {
    rule: PresenceRule;
    action: TimeoutAction;
    deadline: number;
}

/** Parses a stored prompt outcome; anything malformed is null. */
export function parsePromptOutcome(raw: string | null): IPromptOutcome | null {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (PRESENCE_RULES.includes(parsed?.rule) && TIMEOUT_ACTIONS.includes(parsed?.action) && typeof parsed?.deadline === 'number') {
            return { rule: parsed.rule, action: parsed.action, deadline: parsed.deadline };
        }
    } catch {
        // malformed value
    }
    return null;
}

/** Tells other tabs which timeout action the claiming tab ran, so they can follow (lobby / sign out). */
export function writePromptOutcome(outcome: IPromptOutcome): void {
    safeSet(PRESENCE_PROMPT_OUTCOME_STORAGE_KEY, JSON.stringify(outcome));
}

export function readPromptOutcome(): IPromptOutcome | null {
    return parsePromptOutcome(safeGet(PRESENCE_PROMPT_OUTCOME_STORAGE_KEY));
}

const LAST_BERLIN_DATE_KEY = 'inactivityRulesLastBerlinDate';

/** Berlin date (YYYY-MM-DD) this tab last saw, kept in sessionStorage so it survives reloads. Null if unset or unavailable. */
export function readLastBerlinDate(): string | null {
    try {
        return sessionStorage.getItem(LAST_BERLIN_DATE_KEY);
    } catch {
        return null;
    }
}

export function writeLastBerlinDate(date: string): void {
    try {
        sessionStorage.setItem(LAST_BERLIN_DATE_KEY, date);
    } catch {
        // storage unavailable
    }
}

const RELOAD_WITHOUT_ACTIVITY_KEY = 'reloadWithoutActivity';

/**
 * Marks the upcoming page load (this tab only, via sessionStorage) as the automatic reload
 * SocketProvider triggers after a network reconnect, so it isn't mistaken for user activity.
 */
export function markReloadWithoutActivity(): void {
    try {
        sessionStorage.setItem(RELOAD_WITHOUT_ACTIVITY_KEY, 'true');
    } catch {
        // storage unavailable
    }
}

// Captured when the module loads, before SocketProvider's effect consumes the marker, so every reader in this
// document gets the same answer regardless of effect order.
const IS_RECONNECT_RELOAD_DOCUMENT = (() => {
    try {
        return sessionStorage.getItem(RELOAD_WITHOUT_ACTIVITY_KEY) === 'true';
    } catch {
        return false;
    }
})();

/** True when this page load is the automatic reload SocketProvider triggers after a network reconnect. */
export function isReconnectReloadDocument(): boolean {
    return IS_RECONNECT_RELOAD_DOCUMENT;
}

/** Reads and clears the reload marker. True exactly once, for the load right after the marked reload. */
export function consumeReloadWithoutActivity(): boolean {
    try {
        const value = sessionStorage.getItem(RELOAD_WITHOUT_ACTIVITY_KEY);
        sessionStorage.removeItem(RELOAD_WITHOUT_ACTIVITY_KEY);
        return value === 'true';
    } catch {
        return false;
    }
}
